import { FC, useCallback, useContext, useEffect, useState, useId } from "react";

import { SelectChangeEvent } from "@mui/material/Select";
import { browserDefaultTimezone, FormType } from "@simplyk/common";

import { FrontendTicketingContext } from "../../../../contexts/FrontendTicketingContext";
import { generateMenuItemOccurrences } from "../../../../helpers/ticketingOccurrence";
import { usePreviewContext } from "../../../LiveFormEditor/LivePreview/context/PreviewContext";
import { GHOST_PREVIEW_OPACITY } from "../../../LiveFormEditor/LivePreview/ghostPreview.constant";
import { useIsGhostPreview } from "../../../LiveFormEditor/LivePreview/useIsGhostPreview";
import { useFormV2Context } from "../../context/FormV2Context";

import { useOccurrenceSelector } from "./OccurenceSelectorContext";

import {
  FormDateSelectField,
  FormDateSelectItem,
  FormDateSelectItemLabel,
} from "@/forms-design-system/FormDateSelectField";

export const TicketingV2OccurrenceSelector: FC = () => {
  const { selectedOccurrenceId, setSelectedOccurrenceId, occurrencesWithRates } = useContext(FrontendTicketingContext);
  const { isOccurrenceSelectorForcedOpen, setOccurrenceSelectorForcedOpen } = useOccurrenceSelector();
  const { resetForm, form } = useFormV2Context(FormType.Ticketing);

  const { isPreview } = usePreviewContext();
  const isGhostPreview = useIsGhostPreview("TicketingV2OccurrenceSelector");

  const id = useId();

  const menuItemOccurrences = generateMenuItemOccurrences(occurrencesWithRates);
  const totalSelectableOccurrences = menuItemOccurrences.filter((occ) => !occ.isDisabled && !occ.isClosed).length;

  const [isOpen, setIsOpen] = useState(false);

  const handleOnChange = useCallback(
    (event: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
      const occurrenceId = event.target.value as string;
      setSelectedOccurrenceId(occurrenceId);
      setOccurrenceSelectorForcedOpen(false);
      resetForm();
    },
    [setOccurrenceSelectorForcedOpen, resetForm, setSelectedOccurrenceId]
  );

  // Open the select if the context signals it
  useEffect(() => {
    if (isOccurrenceSelectorForcedOpen) {
      setIsOpen(true);
      setOccurrenceSelectorForcedOpen(false);
    }
  }, [isOccurrenceSelectorForcedOpen, setOccurrenceSelectorForcedOpen]);

  const getDate = (date: number | Date | null | undefined) => {
    return date ? new Date(date) : new Date(); // The date should never be null / undefined, but fixing the type would be costly, so we leave it as-is for now
  };

  const handleRenderValue = useCallback(
    (occurrenceId: string) => {
      const item = menuItemOccurrences.find((occ) => occ.occurrenceId === occurrenceId)!;

      return (
        <FormDateSelectItemLabel
          startDate={getDate(item.startUtc)}
          endDate={getDate(item.endUtc)}
          isSoldOut={Boolean(item.isDisabled)}
          isClosed={Boolean(item.isClosed)}
        />
      );
    },
    [menuItemOccurrences]
  );

  if (occurrencesWithRates.length <= 1) {
    return <input type="hidden" {...form.register("occurrenceId")} value={selectedOccurrenceId || ""} />;
  }

  return (
    <FormDateSelectField
      id={id}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      value={selectedOccurrenceId ?? ""}
      timezone={browserDefaultTimezone}
      disableInjectingGlobalStyles={isPreview} // We need this to make the LivePreview smooth
      count={totalSelectableOccurrences}
      sx={{
        opacity: isGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
      }}
      renderValue={handleRenderValue}
      components={{
        select: {
          "data-test": "ticketing-occurrence-selector-select",
        },
      }}
      onChange={handleOnChange}
    >
      {menuItemOccurrences.map((occurrence) => (
        <FormDateSelectItem
          key={occurrence.occurrenceId}
          value={occurrence.occurrenceId}
          startDate={getDate(occurrence.startUtc)}
          endDate={getDate(occurrence.endUtc)}
          isSoldOut={Boolean(occurrence.isDisabled)}
          isClosed={Boolean(occurrence.isClosed)}
          wrapOnMobile
        />
      ))}
    </FormDateSelectField>
  );
};
